import { useEffect, useMemo, useState } from "react";
import { Badge, CloseButton, OverlayTrigger, Popover } from "react-bootstrap";
import styled from "styled-components";
import SntLink from "@/components/ReactBootstrap/SntLink";
import SntDiagnosticWarningIcon from "@icons/SntDiagnosticWarningIcon";
import SntDiagnosticFailureIcon from "@icons/SntDiagnosticFailureIcon";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface StyledBadgeProps {
  size?: "sm" | "md";
}

const BadgeStyle = styled(Badge)<StyledBadgeProps>`
  font-size: ${(props) => (props.size === "sm" ? "0.75rem" : "0.8125rem")};
  font-weight: normal;
  border-radius: 6px;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  height: auto;
  white-space: nowrap;
  vertical-align: middle;
  padding-left: 0.6em;
  padding-right: 0.6em;
  display: inline-flex;
  align-items: center;
`;

const DefaultStyle = styled(Badge)<StyledBadgeProps>`
  font-size: ${(props) => (props.size === "sm" ? "0.75rem" : "0.8125rem")};
  font-weight: normal;
  border-radius: 6px;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  height: auto;
  white-space: nowrap;
  vertical-align: middle;

  background: #e7e7e1 !important;
  color: #424b59 !important;
  border: none !important;
`;

interface Tag {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface TagMoreProps {
  render?: (
    tag: Tag,
    index: number,
    getDiagnosticLabel?: (metricKey: string) => string
  ) => React.ReactElement;
  tags?: Tag[];
  visibleNumber?: number;
  moreClass?: string;
  title?: string;
  onClickMoreLink?: () => void;
}

export const TagMore: React.FC<TagMoreProps> = ({
  render,
  tags = [],
  visibleNumber = 4,
  moreClass = "snt-tags",
  title = "All Tags",
  onClickMoreLink,
}) => {

  const limitedTags = useMemo(() => {
    return tags
      .filter((t, i) => i < visibleNumber)
      .map((tag, index) => {
        return render ? (
          render(tag, index, getDiagnosticLabelByMetricKey)
        ) : (
          <DefaultTag className="me-2 mb-2" key={index} tagName={tag.name} />
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, visibleNumber]);

  const [showPopover, setShowPopover] = useState<boolean>(false);

  useEffect(() => {
    setShowPopover(false);
  }, [tags]);

  const expandedTags = useMemo(() => {
    if (tags.length <= visibleNumber) return null;
    const _expandedTags = tags
      .filter((t, i) => i >= visibleNumber)
      .map((tag, index) => {
        return render ? (
          render(tag, index, getDiagnosticLabelByMetricKey)
        ) : (
          <DefaultTag className="me-2 mb-2" key={index} tagName={tag.name} />
        );
      });

    return (
      <>
        {_expandedTags.length > 0 && (
          <OverlayTrigger
            trigger="click"
            placement="top"
            show={showPopover}
            rootClose={true}
            overlay={
              <Popover>
                <Popover.Header as="h3">
                  {title}
                  <CloseButton
                    className="float-end"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      setShowPopover(false);
                    }}
                  />
                </Popover.Header>
                <Popover.Body
                  className="overflow-auto"
                  style={{ maxHeight: 250 }}
                >
                  {_expandedTags}
                </Popover.Body>
              </Popover>
            }
          >
            <SntLink
              id={"tag" + Date.now()}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (onClickMoreLink) {
                  onClickMoreLink();
                } else {
                  setShowPopover(!showPopover);
                }
              }}
              className={moreClass}
            >
              {`+${tags.length - visibleNumber} ${translate(LanguageKey.more_key)}`}
            </SntLink>
          </OverlayTrigger>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, visibleNumber, showPopover]);

  function getDiagnosticLabelByMetricKey(metricKey: string): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const keyToLabel: { [key: string]: any } = {
      messageCompleteness: translate(LanguageKey.message_transmission_success),
      hwFailure: translate(LanguageKey.hw_failure_key),
      networkQuality: translate(LanguageKey.network_quality_key),
      liveness: translate(LanguageKey.liveness_key),
      gpsFixResult: translate(LanguageKey.gps_fix_success_key),
      sensorCompletenessRatio: translate(
        LanguageKey.sensor_data_completeness_key
      ),
      journeyCompletenessRatio: translate(
        LanguageKey.journey_completeness_ratio_key
      ),
      wifiResult: translate(LanguageKey.wifi_resolution_success_key),
      shortStopRatio: translate(LanguageKey.short_stop_key),
      totalWithoutThrottlingEvent: translate(
        LanguageKey.days_without_throttling_ratio_key
      ),
      hoursWithoutOfflineRatio: translate(LanguageKey.time_online_key),
      averageBatteryConsumptionInADay: translate(
        LanguageKey.average_battery_consumption_in_a_day_key
      ),
      averageNumberOfSendMessageInADay: translate(
        LanguageKey.average_number_of_send_message_day_key
      ),
      ibeaconScanCompleteness: translate(
        LanguageKey.ibeacon_scan_completeness_key
      ),
      ibeaconScanOnTimeRatio: translate(LanguageKey.ibeacon_scan_on_time_key),
      numberOfCellularMismatch: translate(
        LanguageKey.total_number_of_cellular_mismatch
      ),
    };
    return keyToLabel[metricKey] != null ? keyToLabel[metricKey] : metricKey;
  }

  return (
    <>
      {limitedTags}
      {expandedTags}
    </>
  );
};

export default TagMore;

interface AssetTagMoreProps {
  tags?: Tag[];
  visibleNumber?: number;
  moreClass?: string;
  title?: string;
  onClickMoreLink?: () => void;
}

export const AssetTagMore: React.FC<AssetTagMoreProps> = (props) => {
  return (
    <TagMore
      {...props}
      render={(tag, index) => (
        <AssetTag key={index} tagName={tag.name} className="me-2 mb-2" />
      )}
    />
  );
};

interface TagProps {
  tagName?: string;
  title?: string;
  children?: React.ReactNode;
  size?: "sm" | "md";
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  style?: React.CSSProperties;
}

export const DefaultTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg="white"
      className={`border border-sensolus-grey ${className}`}
      style={{ color: "var(--bs-body-color)", ...style }}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const WarningIconTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg="white"
      className={`border border-sensolus-grey ${className}`}
      style={{ color: "var(--bs-body-color)", ...style }}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      <SntDiagnosticWarningIcon />
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const DangerIconTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg="white"
      className={`border border-sensolus-grey ${className}`}
      style={{ color: "var(--bs-body-color)", ...style }}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      <SntDiagnosticFailureIcon />
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const AssetTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <DefaultStyle
      size={size}
      className={`border border-sensolus-grey ${className}`}
      style={{ ...style }}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </DefaultStyle>
  );
};

export const PrimaryTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-sensolus ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
      style={style}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const SecondaryTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-sensolus-secondary ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const GreyTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-sensolus-grey ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const OrangeTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-sensolus-orange ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const SuccessTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      style={style}
      className={`badge-soft-success ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const WarningTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-warning ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const DangerTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-danger ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

/**
 * Solid Sensolus primary — the navy of the primary buttons — with white text.
 * Uses the `sensolus` theme colour ($sensolus-primary), never a hard-coded hex.
 */
export const SensolusTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
  style,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg="sensolus"
      className={className}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
      style={style}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

export const BlueTag: React.FC<TagProps> = ({
  tagName,
  title,
  children,
  size = "md",
  className = "",
  onClick,
}) => {
  return (
    <BadgeStyle
      size={size}
      bg=""
      className={`badge-soft-sensolus-blue me-2 ${className}`}
      title={title || tagName}
      onClick={onClick}
      role={onClick ? "button" : ""}
    >
      {children ? children : tagName}
    </BadgeStyle>
  );
};

interface CustomTagProps {
  className?: string;
  tagName?: string;
  title?: string;
  children?: React.ReactNode;
  bg?: string;
  color?: string;
  border?: string;
  size?: "sm" | "md";
}

export const CustomTag: React.FC<CustomTagProps> = ({
  className = "",
  tagName,
  title,
  children,
  bg,
  color,
  border,
  size = "md",
}) => {
  return (
    <BadgeStyle
      className={`me-2 ${className}`}
      size={size}
      title={title || tagName}
      ref={(el: HTMLSpanElement | null) => {
        if (el) {
          if (bg) el.style.setProperty("background-color", bg, "important");
          if (color) el.style.setProperty("color", color, "important");
          if (border) el.style.setProperty("border", border, "important");
        }
      }}
    >
      <span>{children ? children : tagName}</span>
    </BadgeStyle>
  );
};
